import React from 'react';
import {Grid} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {SwitchCustom, TextBox} from '../../../Core/UI';
import {observer} from 'mobx-react-lite';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {Approval} from '../../../Core/mobx/ParameterStore/Approval';
import {CategoryCard} from '../CategoryCard';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/ApprovalXref';

const useStyles = makeStyles({
    subTitle: {
        fontSize: 20,
        color: '#5D6CC1',
        fontWeight: 'bold',
        marginTop: '1em',
    },
});

type FGHealthProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Approval;
    setCategory: (entry: string) => void;
    index: number;
};

const FGHealth = observer(({valueChanged, parameters, setCategory, index}: FGHealthProps) => {
    const classes = useStyles();
    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );
    return (
        <CategoryCard title={'Health History'} setCategory={setCategory} index={index}>
            <Grid item xs={12}>
                <strong>RX Notes</strong>
            </Grid>
            <Grid item xs={12}>
                <TextBox
                    id={'medicationHistory'}
                    label={required('RX Notes: Medication, Condition, Dosage, Frequency, Beginning Date of Medication')}
                    value={parameters.medicationHistory?.value || 'NA'}
                    multiline
                    rows={10}
                    onChange={(value) => valueChanged(parameters.medicationHistory, value)}
                />
            </Grid>
            <Grid item xs={12}>
                <div className={classes.subTitle}>Last 5 Years</div>
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgBeenAdvicedDiagnosticTestTreatmentSurgeryOrOtherProcedureWhichNotBeenPerformed',
                        label: 'Been advised by a member of the medical profession to have any diagnostic test, treatment, surgery or other procedure which has not been performed? (Excludes tests related to HIV).',
                        ...parameters.fgBeenAdvicedDiagnosticTestTreatmentSurgeryOrOtherProcedureWhichNotBeenPerformed,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={
                        parameters.fgBeenAdvicedDiagnosticTestTreatmentSurgeryOrOtherProcedureWhichNotBeenPerformedDetail
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgBeenTreatedDiagnosedMenthalPhysicalDisorder',
                        label: 'Been treated or diagnosed by a member of the medical profession with any mental or physical disorder?',
                        ...parameters.fgBeenTreatedDiagnosedMenthalPhysicalDisorder,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={parameters.fgBeenTreatedDiagnosedMenthalPhysicalDisorderDetail}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgHadEKGXRayLabTestTreatmentProcedure',
                        label: 'Had any electrocardiogram (EKG), x-ray, laboratory test, treatment, or procedure? (Excludes tests related to the Human Immunodeficiency Virus (HIV)).',
                        ...parameters.fgHadEKGXRayLabTestTreatmentProcedure,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={parameters.fgHadEKGXRayLabTestTreatmentProcedureDetail}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgBeenHospitilezedOrHadSurgeryOrProcedure',
                        label: 'Been hospitalized or had any surgery or procedure?',
                        ...parameters.fgBeenHospitilezedOrHadSurgeryOrProcedure,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={parameters.fgBeenHospitilezedOrHadSurgeryOrProcedureDetail}
                />
            </Grid>
            <Grid item xs={12}>
                <div className={classes.subTitle}>Any Time</div>
            </Grid>
            <Grid item xs={parameters.HIV.value ? 12 : 4}>
                <SwitchCustom
                    param={{
                        ...parameters.HIV,
                        label: 'AIDS or HIV',
                        name: 'HIV',
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={parameters.HIVDetail}
                    provideDetailParamTitle="AIDS Or HIV: Please Provide More Details"
                />
            </Grid>
            <Grid item xs={parameters.HIV.value ? 12 : 8}>
                <SwitchCustom
                    param={{
                        name: 'TobaccoRating',
                        label: 'Tobacco or nicotine in any form?',
                        ...parameters.TobaccoRating,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            {parameters.TobaccoRating.value && (
                <>
                    <Grid item xs={4}>
                        <SelectInputElement
                            param={{
                                label: 'Tobacco: Product Used',
                                name: 'tobaccoProducts',
                                options: fieldOptions.tobaccoProducts.options,
                                ...parameters.tobaccoProducts,
                            }}
                            required={required('')}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <SelectInputElement
                            param={{
                                label: 'Tobacco: Frequency Of Usage',
                                name: 'tobaccoFrequency',
                                options: fieldOptions.tobaccoFrequency.options,
                                ...parameters.tobaccoFrequency,
                            }}
                            required={required('')}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <DatePicker
                                inputVariant="outlined"
                                variant="inline"
                                openTo="year"
                                views={['year', 'month']}
                                label={required('Tobacco: Last Usage')}
                                style={{width: '100%', marginTop: 8}}
                                value={parameters.tobaccoLastUse?.value || null}
                                onChange={(date) => valueChanged(parameters.tobaccoLastUse, date)}
                            />
                        </MuiPickersUtilsProvider>
                    </Grid>
                </>
            )}
            <Grid item xs={12}>
                <p>
                    Has the Proposed Insured ever (1) been diagnosed with, or (2) received treatment for, or (3) been
                    advised by a member of the medical profession to seek treatment for:
                </p>
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgHeartBloodVesselsDisorder',
                        label: 'Any disorder of the heart or blood vessels including but not limited to coronary artery disease, heart attack, heart failure, chest pain, irregular heartbeat, valvular heart disease, congenital heart disease or defect, heart murmur, high blood pressure, or high cholesterol?',
                        ...parameters.fgHeartBloodVesselsDisorder,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={parameters.fgHeartBloodVesselsDisorderDetail}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgCirculatorySystemDisorder',
                        label: 'Any disorder of the circulatory system including but not limited to stroke, transient ischemic attack (TIA), aneurysm, carotid artery disease, or peripheral vascular disease?',
                        ...parameters.fgCirculatorySystemDisorder,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={parameters.fgCirculatorySystemDisorderDetail}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgLungsOrRespiratorySystemDisorder',
                        label: 'Any disorder of the lungs or respiratory system including but not limited to asthma, chronic bronchitis, chronic obstructive pulmonary disease (COPD), emphysema, tuberculosis, or sleep apnea?',
                        ...parameters.fgLungsOrRespiratorySystemDisorder,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={parameters.fgLungsOrRespiratorySystemDisorderDetail}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgImmuneSystemOrEndocrineSystemDisorder',
                        label: 'Any disorder of the immune system or endocrine system including but not limited to diabetes, anemia, blood disorder, or thyroid disorder (except those related to AIDS Virus)?',
                        ...parameters.fgImmuneSystemOrEndocrineSystemDisorder,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={parameters.fgImmuneSystemOrEndocrineSystemDisorderDetail}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgCancerTumorsPolypsCysts',
                        label: 'Cancer, tumors, polyps, or cysts?',
                        ...parameters.fgCancerTumorsPolypsCysts,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={parameters.fgCancerTumorsPolypsCystsDetail}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgPsychiatricOrMentalDisorder',
                        label: 'Any psychiatric or mental health disorder including but not limited to anxiety, depression, bipolar disorder, schizophrenia, or post-traumatic stress disorder?',
                        ...parameters.fgPsychiatricOrMentalDisorder,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={parameters.fgPsychiatricOrMentalDisorderDetail}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgLupusOrOtherConnectiveTissueDiseaseAutoimmuneDisorder',
                        label: 'Lupus or other connective tissue disease; any autoimmune disorder?',
                        ...parameters.fgLupusOrOtherConnectiveTissueDiseaseAutoimmuneDisorder,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={parameters.fgLupusOrOtherConnectiveTissueDiseaseAutoimmuneDisorderDetail}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgStomachLiverIntestinesColonPancreasDiseaseDisorder',
                        label: 'Any disease or disorder of the stomach, liver, intestines/colon, or pancreas including but not limited to ulcer, hepatitis, Crohn’s disease, or ulcerative colitis?',
                        ...parameters.fgStomachLiverIntestinesColonPancreasDiseaseDisorder,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={parameters.fgStomachLiverIntestinesColonPancreasDiseaseDisorderDetail}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgKidneysBladderUrinarySystemProstateBreastsReproductiveSystemDiseaseDisorder',
                        label: 'Any disease or disorder of the kidneys, bladder or urinary system; prostate, breasts, or reproductive system?',
                        ...parameters.fgKidneysBladderUrinarySystemProstateBreastsReproductiveSystemDiseaseDisorder,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={
                        parameters.fgKidneysBladderUrinarySystemProstateBreastsReproductiveSystemDiseaseDisorderDetail
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgMuscleBonesSpineJointsDiseaseDisorder',
                        label: 'Any disease or disorder of the muscle, bones, spine, or joints including but not limited to arthritis, fibromyalgia, or chronic pain?',
                        ...parameters.fgMuscleBonesSpineJointsDiseaseDisorder,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={parameters.fgMuscleBonesSpineJointsDiseaseDisorderDetail}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'fgSkinEyesEarsNoseThroatDiseaseDisorder',
                        label: 'Any disease or disorder of the skin, eyes, ears, nose or throat?',
                        ...parameters.fgSkinEyesEarsNoseThroatDiseaseDisorder,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    provideDetailParam={parameters.fgSkinEyesEarsNoseThroatDiseaseDisorderDetail}
                />
            </Grid>
        </CategoryCard>
    );
});

export default FGHealth;
